import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowUpRight, ArrowRight, Download, ShieldCheck, Landmark, Users, TrendingUp } from "lucide-react";
import Seo from "../components/site/Seo";
import { MaskLines, Reveal, Stat } from "../components/site/Reveal";
import { SectionLabel } from "../components/site/Section";
import TrustSignals from "../components/site/TrustSignals";
import { SERVICES, INDUSTRIES, MANIFESTO, STATS, PHILOSOPHY } from "../data/site";

const PILLARS = [
  { icon: Landmark, title: "Government Contracting", body: "Cleared talent, proposal staffing, and compliance-aligned workforce support across the federal contract lifecycle.", to: "/government" },
  { icon: Users, title: "Staffing Solutions", body: "Direct hire, executive search, contingent, and managed workforce programs deployed with rigor and speed.", to: "/services" },
  { icon: TrendingUp, title: "Business Solutions", body: "Workforce planning, human capital consulting, and organizational design that turn talent into advantage.", to: "/services" },
];

export default function Home() {
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);

  return (
    <div data-testid="home-page">
      <Seo title="National Workforce Solutions" description="VectorBridge Group LLC delivers government contracting, staffing, and business solutions for government agencies, defense contractors, healthcare systems, and enterprise organizations nationwide." />

      {/* HERO */}
      <section ref={heroRef} className="relative min-h-[100svh] overflow-hidden bg-background">
        <div className="tech-grid pointer-events-none absolute inset-0 opacity-40 mask-fade-b" />
        <div className="pointer-events-none absolute -right-40 top-0 h-[600px] w-[600px] rounded-full bg-lightblue/20 blur-[120px]" />
        <div className="relative z-10 mx-auto grid min-h-[100svh] max-w-[1400px] items-center gap-12 px-5 pt-32 pb-16 sm:px-8 lg:grid-cols-12">
          <div className="lg:col-span-7">
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.2 }}>
              <SectionLabel index="01">National Workforce Solutions · Government-Ready</SectionLabel>
            </motion.div>

            <h1 className="mt-8 font-display text-[15vw] font-bold uppercase leading-[0.9] tracking-tighter text-navy sm:text-[6rem]">
              <MaskLines lines={["Building bridges."]} delay={0.3} />
              <MaskLines lines={["Delivering results."]} delay={0.5} className="text-brand" />
            </h1>

            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.9, duration: 0.8 }}
              className="mt-8 max-w-xl text-lg leading-relaxed text-muted-foreground"
            >
              VectorBridge Group LLC delivers government contracting, staffing, and business
              solutions to the agencies and enterprises carrying mission-critical work — capability
              engineered for compliance, clearance, and outcomes.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1.05, duration: 0.8 }}
              className="mt-10 flex flex-wrap gap-3"
            >
              <Link to="/government" data-testid="hero-gov-cta" className="group inline-flex items-center gap-2 bg-brand px-6 py-4 font-mono text-xs uppercase tracking-[0.15em] text-white transition-colors hover:bg-navy">
                Government Solutions <ArrowUpRight size={16} className="transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </Link>
              <Link to="/contact" data-testid="hero-client-cta" className="inline-flex items-center gap-2 border border-border bg-white px-6 py-4 font-mono text-xs uppercase tracking-[0.15em] text-navy transition-colors hover:border-navy">
                Engage Our Team
              </Link>
            </motion.div>
          </div>

          <motion.div style={{ y }} className="hidden items-center justify-center lg:col-span-5 lg:flex">
            <motion.img
              src="/vbg-logo-navy-t.png"
              alt="VectorBridge Group"
              draggable={false}
              initial={{ opacity: 0, scale: 0.94 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.4, duration: 1.1, ease: [0.16, 1, 0.3, 1] }}
              className="pointer-events-none w-full max-w-[440px] select-none"
            />
          </motion.div>
        </div>
      </section>

      {/* THREE PILLARS */}
      <section className="border-b border-border bg-secondary/50">
        <div className="mx-auto max-w-[1400px] px-5 py-24 sm:px-8 sm:py-32">
          <div className="flex flex-col justify-between gap-6 sm:flex-row sm:items-end">
            <div>
              <SectionLabel index="02">What We Do</SectionLabel>
              <Reveal><h2 className="mt-6 font-display text-4xl font-light tracking-tighter text-navy sm:text-5xl">Three pillars. One standard.</h2></Reveal>
            </div>
            <p className="max-w-md font-mono text-[11px] uppercase tracking-[0.18em] text-muted-foreground">Government Contracting · Staffing · Business Solutions</p>
          </div>
          <div className="mt-14 grid gap-px bg-border sm:grid-cols-3">
            {PILLARS.map((p, i) => {
              const Icon = p.icon;
              return (
                <Reveal key={p.title} delay={i * 0.08} className="group bg-background p-8 transition-colors hover:bg-white sm:p-10">
                  <div className="grid h-16 w-16 place-items-center rounded-full bg-navy transition-transform duration-500 group-hover:scale-105">
                    <Icon size={28} className="text-white" strokeWidth={1.6} />
                  </div>
                  <h3 className="mt-8 font-display text-2xl tracking-tight text-navy">{p.title}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-muted-foreground">{p.body}</p>
                  <Link to={p.to} data-testid={`pillar-${i}`} className="mt-6 inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.15em] text-brand transition-colors group-hover:gap-3">
                    Learn more <ArrowRight size={14} />
                  </Link>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* WHY VECTORBRIDGE EXISTS */}
      <section className="border-b border-border">
        <div className="mx-auto max-w-[1400px] px-5 py-24 sm:px-8 sm:py-32">
          <div className="grid gap-12 md:grid-cols-12">
            <div className="md:col-span-4">
              <div className="md:sticky md:top-28">
                <span className="font-mono text-[11px] uppercase tracking-[0.25em] text-brand">Philosophy</span>
                <Reveal delay={0.05}>
                  <h2 className="mt-6 font-display text-3xl font-light tracking-tighter text-navy sm:text-4xl">
                    Why VectorBridge exists.
                  </h2>
                </Reveal>
              </div>
            </div>
            <div className="md:col-span-8">
              <Reveal>
                <p className="max-w-3xl border-l-2 border-brand pl-8 font-display text-2xl font-light leading-snug tracking-tight text-navy sm:text-4xl sm:leading-[1.25]">
                  {PHILOSOPHY}
                </p>
              </Reveal>
            </div>
          </div>
        </div>
      </section>

      {/* MANDATE / STATS */}
      <section className="border-b border-border bg-secondary/50 py-24 sm:py-32">
        <div className="mx-auto max-w-[1400px] px-5 sm:px-8">
          <div className="grid gap-12 md:grid-cols-12">
            <div className="md:col-span-7">
              <SectionLabel index="03">Mandate</SectionLabel>
              <Reveal>
                <h2 className="mt-8 max-w-3xl font-display text-3xl font-light leading-tight tracking-tight text-navy sm:text-5xl">
                  We build workforces that perform under scrutiny — where compliance is
                  non-negotiable and the mission cannot wait.
                </h2>
              </Reveal>
            </div>
            <div className="grid grid-cols-2 gap-8 md:col-span-5 md:grid-cols-2">
              {STATS.map((s, i) => (
                <Stat key={s.label} value={s.k} label={s.label} delay={i * 0.08} />
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CAPABILITIES PREVIEW */}
      <section className="border-b border-border">
        <div className="mx-auto max-w-[1400px] px-5 py-24 sm:px-8 sm:py-32">
          <div className="flex flex-col justify-between gap-6 sm:flex-row sm:items-end">
            <div>
              <SectionLabel index="04">Capabilities</SectionLabel>
              <Reveal><h2 className="mt-6 font-display text-4xl font-light tracking-tighter text-navy sm:text-6xl">Capability, delivered.</h2></Reveal>
            </div>
            <Link to="/services" data-testid="home-services-link" className="group inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.15em] text-muted-foreground hover:text-brand">
              All capabilities <ArrowRight size={16} className="transition-transform group-hover:translate-x-1" />
            </Link>
          </div>
          <div className="mt-14 grid border-l border-t border-border sm:grid-cols-2 lg:grid-cols-3">
            {SERVICES.slice(0, 6).map((s, i) => (
              <Reveal key={s.id} delay={(i % 3) * 0.06} className="group relative border-b border-r border-border p-8 transition-colors hover:bg-secondary">
                {i < 3 && <span className="absolute left-0 top-0 h-full w-[3px] bg-brand" aria-hidden="true" />}
                <div className="flex items-start justify-between">
                  <span className="font-mono text-xs text-brand">{s.id}</span>
                  <ArrowUpRight size={18} className="text-muted-foreground transition-transform group-hover:-translate-y-1 group-hover:translate-x-1" />
                </div>
                <h3 className="mt-10 font-display text-2xl tracking-tight text-navy">{s.title}</h3>
                <p className="mt-3 text-sm leading-relaxed text-muted-foreground">{s.desc}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* INDUSTRIES */}
      <section className="border-b border-border">
        <div className="mx-auto max-w-[1400px] px-5 py-24 sm:px-8 sm:py-32">
          <SectionLabel index="05">Sectors Served</SectionLabel>
          <div className="mt-14 grid gap-px bg-border md:grid-cols-3">
            {INDUSTRIES.map((ind, i) => (
              <Reveal key={ind.name} delay={(i % 3) * 0.08} className="group relative aspect-[4/5] overflow-hidden bg-navy">
                <img src={ind.img} alt={ind.name} className="absolute inset-0 h-full w-full object-cover opacity-45 transition-all duration-700 group-hover:scale-105 group-hover:opacity-65" />
                <div className="absolute inset-0 bg-gradient-to-t from-navy via-navy/40 to-transparent" />
                <div className="relative flex h-full flex-col justify-end p-7">
                  <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-lightblue">{ind.tag}</div>
                  <h3 className="mt-2 font-display text-2xl tracking-tight text-white">{ind.name}</h3>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.1}>
            <Link to="/industries" data-testid="home-industries-link" className="group mt-10 inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.15em] text-muted-foreground hover:text-brand">
              Explore sectors <ArrowRight size={16} className="transition-transform group-hover:translate-x-1" />
            </Link>
          </Reveal>
        </div>
      </section>

      {/* GOVERNMENT CREDIBILITY — NAVY BAND */}
      <section className="relative overflow-hidden bg-navy text-white">
        <div className="tech-grid pointer-events-none absolute inset-0 opacity-[0.07]" />
        <div className="relative mx-auto grid max-w-[1400px] gap-12 px-5 py-24 sm:px-8 sm:py-32 md:grid-cols-2">
          <div>
            <div className="flex items-center gap-4 font-mono text-[11px] uppercase tracking-[0.25em] text-lightblue">
              <span className="h-px w-8 bg-lightblue" /> Government &amp; Defense
            </div>
            <Reveal>
              <h2 className="mt-8 max-w-lg font-display text-4xl font-light leading-tight tracking-tighter sm:text-5xl">
                Built for the standards the federal mission demands.
              </h2>
            </Reveal>
            <Reveal delay={0.1}>
              <p className="mt-6 max-w-md text-white/70">
                From proposal staffing and contingent hire commitments to cleared and clearable talent pipelines,
                we align to FAR requirements, contract vehicles, and the compliance posture your program office expects.
              </p>
            </Reveal>
            <Reveal delay={0.15}>
              <Link to="/government" data-testid="home-government-link" className="group mt-8 inline-flex items-center gap-2 bg-white px-6 py-4 font-mono text-xs uppercase tracking-[0.15em] text-navy transition-colors hover:bg-lightblue hover:text-white">
                Government Solutions <ArrowUpRight size={16} className="transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </Link>
            </Reveal>
          </div>
          <div className="grid gap-px self-center bg-white/10">
            {["Proposal & surge staffing support", "Cleared & clearable candidate pipelines", "Contract vehicle & compliance alignment", "Key personnel & letters of intent"].map((t, i) => (
              <Reveal key={t} delay={i * 0.06} className="flex items-center gap-4 bg-navy p-6">
                <ShieldCheck size={20} className="shrink-0 text-lightblue" strokeWidth={1.6} />
                <span className="text-sm">{t}</span>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* MANIFESTO */}
      <section className="border-b border-border">
        <div className="mx-auto max-w-[1400px] px-5 py-24 sm:px-8 sm:py-32">
          <SectionLabel index="07">Quality & Integrity</SectionLabel>
          <Reveal><h2 className="mt-8 max-w-2xl font-display text-4xl font-light tracking-tighter text-navy sm:text-6xl">Our commitments:</h2></Reveal>
          <div className="mt-16 divide-y divide-border border-t border-border">
            {MANIFESTO.map((m, i) => (
              <Reveal key={m.n} delay={i * 0.05}>
                <div className="group grid gap-4 py-10 md:grid-cols-12 md:items-baseline">
                  <span className="font-mono text-sm text-brand md:col-span-1">{m.n}</span>
                  <h3 className="font-display text-3xl tracking-tight text-navy transition-colors group-hover:text-brand md:col-span-4">{m.title}</h3>
                  <p className="max-w-xl text-muted-foreground md:col-span-7">{m.body}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* CAPABILITY STATEMENT */}
      <section className="border-b border-border bg-secondary">
        <div className="mx-auto flex max-w-[1400px] flex-col items-start justify-between gap-8 border-l-2 border-brand px-5 py-20 sm:flex-row sm:items-center sm:px-8">
          <div>
            <SectionLabel index="08">Capability Statement</SectionLabel>
            <h2 className="mt-6 font-display text-3xl font-light tracking-tighter text-navy sm:text-4xl">Download our capabilities overview.</h2>
            <p className="mt-3 max-w-xl text-sm text-muted-foreground">NAICS codes, core competencies, differentiators, and points of contact — formatted for acquisition teams.</p>
          </div>
          <a href="/VectorBridge-Capability-Statement.pdf" download data-testid="home-capability-download" className="group inline-flex shrink-0 items-center gap-3 border border-border bg-white px-7 py-5 font-mono text-xs uppercase tracking-[0.15em] text-navy transition-colors hover:border-brand hover:text-brand">
            <Download size={18} /> Download PDF
          </a>
        </div>
      </section>

      {/* DUAL CTA */}
      <section className="border-b border-border">
        <div className="mx-auto grid max-w-[1400px] gap-px bg-border sm:grid-cols-2">
          <Link to="/contact" data-testid="cta-clients" className="group relative overflow-hidden bg-background p-10 sm:p-16">
            <div className="absolute inset-0 -translate-y-full bg-brand transition-transform duration-500 group-hover:translate-y-0" />
            <div className="relative">
              <div className="font-mono text-[11px] uppercase tracking-[0.2em] text-brand group-hover:text-white">For Organizations</div>
              <h3 className="mt-6 font-display text-4xl tracking-tighter text-navy sm:text-5xl group-hover:text-white">Advance your mission.</h3>
              <p className="mt-4 max-w-sm text-muted-foreground group-hover:text-white/80">Talk to a workforce strategist about your requisitions, contracts, and program needs.</p>
              <span className="mt-8 inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.15em] text-navy group-hover:text-white">Start now <ArrowUpRight size={16} /></span>
            </div>
          </Link>
          <Link to="/careers" data-testid="cta-candidates" className="group relative overflow-hidden bg-background p-10 sm:p-16">
            <div className="absolute inset-0 -translate-y-full bg-navy transition-transform duration-500 group-hover:translate-y-0" />
            <div className="relative">
              <div className="font-mono text-[11px] uppercase tracking-[0.2em] text-brand group-hover:text-lightblue">For Candidates</div>
              <h3 className="mt-6 font-display text-4xl tracking-tighter text-navy sm:text-5xl group-hover:text-white">Advance your career.</h3>
              <p className="mt-4 max-w-sm text-muted-foreground group-hover:text-white/80">Submit your profile and connect with roles across our national client network.</p>
              <span className="mt-8 inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.15em] text-navy group-hover:text-white">Apply now <ArrowUpRight size={16} /></span>
            </div>
          </Link>
        </div>
      </section>

      {/* TRUST SIGNALS — moved to bottom */}
      <TrustSignals />
    </div>
  );
}
